import { Dialog, Transition } from '@headlessui/react'
import { Icon } from '@iconify/react'
import clsx from 'clsx'
import dayjs from 'dayjs'
import { Fragment } from 'react'

import type { SuratKuasaByQueueResData, SuratKuasaDocumentItem, SuratKuasaUnitItem } from '~/types/pickup'

interface ModalSuratKuasaProps {
  isOpen: boolean
  closeModal: () => void
  data: SuratKuasaByQueueResData['data']
}

// Written for the CS at the counter, not for a developer: raw statuses like
// "code_redeemed" mean nothing to them, so every code gets a plain-Indonesian
// label and an empty list says *why* it is empty.
const SURAT_KUASA_STATUS_LABEL: Record<string, string> = {
  submitted: 'Surat kuasa diajukan, kode belum dipakai',
  code_redeemed: 'Kode sudah dipakai, sedang dilayani',
  scheduled: 'Sudah dijadwalkan',
  released: 'Unit/dokumen sudah diserahkan',
  expired: 'Kedaluwarsa',
}

const ITEM_STATUS_LABEL: Record<string, string> = {
  method_selected: 'Cara pengambilan dipilih',
  scheduled: 'Dijadwalkan',
  code_redeemed: 'Menunggu diserahkan',
  released: 'Sudah diserahkan',
  expired: 'Kedaluwarsa',
}

const STATUS_TONE: Record<string, string> = {
  released: 'bg-emerald-100 text-emerald-700',
  code_redeemed: 'bg-blue-100 text-blue-700',
  scheduled: 'bg-amber-100 text-amber-700',
  submitted: 'bg-amber-100 text-amber-700',
  method_selected: 'bg-gray-200 text-gray-700',
  expired: 'bg-red-100 text-red-700',
}

const VEHICLE_TYPE_LABEL: Record<string, string> = {
  car: 'Mobil',
  bike: 'Motor',
}

const formatAuctionDate = (value: string | null) => {
  if (!value) return null

  const date = dayjs(value)

  return date.isValid() ? date.format('DD MMM YYYY') : value
}

const StatusBadge = ({ status, labels }: { status: string | null; labels: Record<string, string> }) => {
  if (!status) return null

  return (
    <span
      className={clsx(
        'inline-block rounded-full px-2 py-0.5 text-[11px] font-bold',
        STATUS_TONE[status] || 'bg-gray-200 text-gray-700',
      )}
    >
      {labels[status] || status}
    </span>
  )
}

const ModalSuratKuasa = ({ isOpen, closeModal, data }: ModalSuratKuasaProps) => {
  // Defensive: this modal sits inside the live CS screen, so a malformed
  // payload (units/documents missing, counts absent) must degrade to an empty
  // list instead of throwing and taking the whole serving panel down with it.
  const units = Array.isArray(data.units) ? data.units : []
  const documents = Array.isArray(data.documents) ? data.documents : []
  const unitsCount = typeof data.units_count === 'number' ? data.units_count : units.length
  const documentsCount = typeof data.documents_count === 'number' ? data.documents_count : documents.length

  const renderIdentity = ({
    label,
    hint,
    nama,
    noKtp,
    noHp,
    highlight,
  }: {
    label: string
    hint: string
    nama: string | null
    noKtp: string | null
    noHp: string | null
    highlight?: boolean
  }) => (
    <div
      className={clsx('rounded-lg border p-3', highlight ? 'border-jba-blue bg-blue-50' : 'border-gray-200 bg-white')}
    >
      <div className="text-xs font-bold text-jba-blue">{label}</div>
      <div className="mb-2 text-[11px] leading-tight text-gray-600">{hint}</div>
      <div className="text-base font-bold leading-tight">{nama || 'Nama tidak tercatat'}</div>
      <dl className="mt-1 space-y-0.5 text-xs text-gray-700">
        <div className="flex gap-1">
          <dt className="shrink-0 text-gray-500">No. KTP:</dt>
          <dd className="font-mono">{noKtp || '-'}</dd>
        </div>
        <div className="flex gap-1">
          <dt className="shrink-0 text-gray-500">No. HP:</dt>
          <dd className="font-mono">{noHp || '-'}</dd>
        </div>
      </dl>
    </div>
  )

  const renderCoveredItem = (key: string | number, item: SuratKuasaUnitItem | SuratKuasaDocumentItem) => {
    const auctionDate = formatAuctionDate(item.auction_date)
    const vehicleLabel = item.vehicle_type ? VEHICLE_TYPE_LABEL[item.vehicle_type] || item.vehicle_type : null

    return (
      <div key={key} className="rounded-lg border border-gray-200 p-2">
        <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-sm font-bold text-jba-blue">Lot {item.lot || '-'}</span>
          <StatusBadge status={item.pickup_status} labels={ITEM_STATUS_LABEL} />
        </div>
        <div className="text-xs font-bold">
          {[item.brand, item.model].filter(Boolean).join(' ') || 'Data kendaraan tidak ditemukan'}
        </div>
        <div className="mt-0.5 space-y-0.5 text-[11px] text-gray-600">
          <div>
            Nomor polisi: <span className="font-mono">{item.license || '-'}</span>
          </div>
          <div>
            {vehicleLabel ? `${vehicleLabel} · ` : ''}
            Tanggal lelang: {auctionDate || '-'}
          </div>
        </div>
      </div>
    )
  }

  const renderEmptyList = (text: string) => (
    <div className="rounded-lg border border-dashed border-gray-300 p-2 text-[11px] leading-tight text-gray-500">
      {text}
    </div>
  )

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={closeModal}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-5xl rounded-2xl bg-white p-6 text-left shadow-xl transition-all">
                <button
                  type="button"
                  className="absolute right-4 top-4 inline-flex h-6 w-6 items-center justify-center rounded-full bg-white text-jba-blue shadow hover:bg-jba-gray"
                  onClick={closeModal}
                >
                  <Icon icon="material-symbols:close-rounded" />
                </button>

                <Dialog.Title className="pr-8 text-xl font-bold text-jba-blue">
                  Surat Kuasa Pengambilan Unit &amp; Dokumen
                </Dialog.Title>
                <p className="mt-0.5 text-xs text-gray-600">
                  Antrean ini dibuat dari kode unik <span className="font-bold">Dikuasakan</span> — unit/dokumen diambil
                  oleh orang lain, bukan pembelinya. Cocokkan KTP orang di counter dengan{' '}
                  <span className="font-bold">Penerima Kuasa</span> di kanan, lalu periksa surat kuasanya di bawah.
                </p>

                <div className="mb-4 mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-700">
                  <span>
                    Kode Unik: <span className="font-mono font-bold">{data.kode_unik || '-'}</span>
                  </span>
                  <span>
                    No. Antrean: <span className="font-bold">{data.antrian_queue_no || '-'}</span>
                  </span>
                  <StatusBadge status={data.status} labels={SURAT_KUASA_STATUS_LABEL} />
                </div>

                <div className="grid gap-4 lg:grid-cols-[1fr_20rem]">
                  <div className="min-w-0">
                    {data.pdf_url ? (
                      <>
                        <iframe
                          src={data.pdf_url}
                          title="Surat Kuasa"
                          className="h-[70vh] w-full rounded-lg border border-gray-200"
                        />
                        {/* Escape hatch: the PDF lives on S3, so any host that
                            sends Cross-Origin-Embedder-Policy: require-corp
                            blocks the iframe above - the link still works. */}
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                          <a
                            href={data.pdf_url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 font-bold text-jba-blue hover:underline"
                          >
                            <Icon icon="material-symbols:open-in-new" />
                            Buka PDF di tab baru
                          </a>
                          <span>kalau dokumen di atas tidak tampil.</span>
                        </div>
                      </>
                    ) : (
                      <div className="flex h-[70vh] flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-gray-300 px-6 text-center">
                        <Icon icon="material-symbols:description-outline" className="text-3xl text-gray-300" />
                        <div className="text-sm font-bold text-gray-500">File surat kuasa belum tersedia</div>
                        <div className="text-xs text-gray-500">
                          Dokumennya gagal dibuat sistem. Data pemberi &amp; penerima kuasa di samping tetap bisa
                          dipakai untuk verifikasi. Lapor ke tim IT kalau file tetap tidak muncul setelah modal ditutup
                          dan dibuka lagi.
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-3 lg:max-h-[calc(70vh+1.75rem)] lg:overflow-y-auto">
                    {renderIdentity({
                      label: 'Penerima Kuasa (yang datang ke counter)',
                      hint: 'Minta KTP aslinya, samakan nama dan nomornya dengan data ini.',
                      nama: data.penerima_nama,
                      noKtp: data.penerima_no_ktp,
                      noHp: data.penerima_no_hp,
                      highlight: true,
                    })}
                    {renderIdentity({
                      label: 'Pemberi Kuasa (pembeli unit)',
                      hint: 'Pemilik unit yang memberi kuasa. Tidak perlu hadir di counter.',
                      nama: data.pemberi_nama,
                      noKtp: data.pemberi_no_ktp,
                      noHp: data.pemberi_no_hp,
                    })}

                    <div className="space-y-2">
                      <div className="text-xs font-bold text-jba-blue">Unit yang boleh diambil ({unitsCount})</div>
                      {units.length > 0
                        ? units.map((unit) => renderCoveredItem(unit.unit_pickup_id, unit))
                        : renderEmptyList('Surat kuasa ini tidak mencakup pengambilan unit kendaraan.')}
                    </div>

                    <div className="space-y-2">
                      <div className="text-xs font-bold text-jba-blue">
                        Dokumen yang boleh diambil ({documentsCount})
                      </div>
                      {documents.length > 0
                        ? documents.map((document) => renderCoveredItem(document.document_pickup_id, document))
                        : renderEmptyList(
                            'Surat kuasa ini tidak mencakup pengambilan dokumen (BPKB/STNK) — hanya unitnya.',
                          )}
                    </div>

                    {units.length === 0 && documents.length === 0 && (
                      <div className="rounded-lg bg-amber-50 p-2 text-[11px] leading-tight text-amber-800">
                        Surat kuasa ini belum menempel ke unit maupun dokumen mana pun. Jangan serahkan apa pun —
                        teruskan ke tim FI/IT untuk dicek dulu.
                      </div>
                    )}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default ModalSuratKuasa
